import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import styled, { css } from "styled-components";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import useInlineEditBuffer from "@tailormed/common-client/component/inline-edit/hooks/useInlineEditBuffer";
import AssigneesSelectField from "@tailormed/common-client/component/Select/AssigneesSelectField";

import { selectAssigneesAndUserGroups, selectUser } from "../../../store/selector";
import { ERROR_600 } from "../../../constant/colors";
import { toAssigneeIdArray } from "../../../utils/formaters";
import getInlineEditPopupContainer from "@tailormed/common-client/component/inline-edit/getInlineEditPopupContainer";

// Mirrors the error presentation of common-client's TextEditor (the Max Benefit editor): the
// message renders under the field and the select gets an error border, instead of a toast.
const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  ${({ $hasError }) =>
    $hasError &&
    css`
      .ant-select .ant-select-selector {
        border-color: ${ERROR_600} !important;
      }
    `}
`;

const ErrorText = styled.div`
  color: ${ERROR_600};
  font-size: 12px;
  line-height: 1.4;
`;

/**
 * `validate` for the entities where an assignee is mandatory (enrollment, claim — never patient,
 * where Unassigned is valid). Shared so the rule and its message live in one place.
 *
 * @param {Function} t - Translation function
 * @param {string} [messageKey] - Override for the claim-specific message
 * @returns {(ids: string[]) => true|string} true when committable, else the message to show
 */
export const buildAssigneeRequiredValidator =
  (t, messageKey = "patientApplications.assignee_required_error") =>
  (ids) =>
    ids.length > 0 ? true : t(messageKey);

// Shared multi-assignee inline editor used by patient, enrollment and claim assignee cells.
// Multi-select parity with the track tab — commits the selected ids as an array.
const AssigneesInlineEditor = ({ value, onCommit, onCancel, autoFocus = true, validate }) => {
  const { t } = useTranslation();
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const user = useSelector(selectUser);
  const [error, setError] = useState(null);

  const initialIds = toAssigneeIdArray(value)
    .map((id) => (assignees || []).find((a) => a && a.id != null && a.id.toString() === id))
    .filter(Boolean)
    .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""))
    .map(({ id }) => String(id));
  const { buffer, setBuffer, commit, cancel } = useInlineEditBuffer({
    value: initialIds,
    onCommit,
    onCancel
  });

  // The deferred Enter path below reads the selection after AntD flushes, so keep a live ref.
  const bufferRef = useRef(buffer);
  useEffect(() => {
    bufferRef.current = buffer;
  }, [buffer]);

  // Same `validate` contract as TextEditor: a returned string is shown under the field and the
  // commit is refused, so the editor stays open on the offending value instead of reverting.
  const attemptCommit = useCallback(() => {
    const next = bufferRef.current;
    if (_.isEqual(next, initialIds)) {
      cancel();
      return;
    }
    const result = typeof validate === "function" ? validate(next) : true;
    if (typeof result === "string" && result.length > 0) {
      setError(result);
      return;
    }
    setError(null);
    commit(next);
  }, [initialIds, validate, commit, cancel]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        cancel();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        // Defer so AntD's pending option-add (Enter on the highlighted option) lands in the
        // buffer first — the same reason useInlineEditBuffer defers its own Enter commit.
        setTimeout(attemptCommit, 0);
      }
    },
    [attemptCommit, cancel]
  );

  const handleChange = useCallback(
    (ids) => {
      setBuffer(ids);
      setError(null);
    },
    [setBuffer]
  );

  return (
    <EditorWrapper $hasError={Boolean(error)}>
      <AssigneesSelectField
        assignees={assignees}
        currentUser={user}
        currentUserSuffix={t("bulk_actions.current_user_suffix")}
        placeholder={t("inline_edit.assignee_placeholder")}
        value={buffer}
        onChange={handleChange}
        autoFocus={autoFocus}
        defaultOpen={autoFocus}
        suffixIcon={<SearchOutlined />}
        maxTagCount="responsive"
        maxTagPlaceholder={(omitted) => `+${omitted.length}`}
        bordered
        dropdownMatchSelectWidth={false}
        dropdownStyle={{ minWidth: 280, maxHeight: 240, overflowY: "auto" }}
        getPopupContainer={getInlineEditPopupContainer}
        style={{ width: "100%", minWidth: 140 }}
        onBlur={attemptCommit}
        onKeyDown={handleKeyDown}
      />
      {error && (
        <ErrorText className="tm-inline-edit-error" role="alert">
          {error}
        </ErrorText>
      )}
    </EditorWrapper>
  );
};

AssigneesInlineEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
  onCommit: PropTypes.func,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  // Returns true when the selection may be committed, or the message to show under the field.
  validate: PropTypes.func
};

export default AssigneesInlineEditor;
